/**
 * useRoleTrainingRecommendations Hook
 * 
 * Fetches training recommendations for advisors and managers.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
// DTC-L: route role-training `actioned_at` writes through clock.
import { clock } from '@/utils/clock';

export interface RoleTrainingRecommendation {
  id: string;
  userId: string;
  shopId: string;
  role: 'advisor' | 'manager';
  artifactId: string | null;
  categoryKey: string;
  reason: string | null;
  priority: number;
  status: 'active' | 'dismissed' | 'completed';
  sourceEventId: string | null;
  createdAt: string;
  actionedAt: string | null;
  // Joined artifact data
  artifact?: {
    id: string;
    title: string;
    artifactType: string;
    accessUrl: string | null;
  };
}

export function useRoleTrainingRecommendations(
  userId: string | undefined,
  role: 'advisor' | 'manager'
) {
  const { activeShop } = useAuth();

  return useQuery({
    queryKey: ['role-training-recommendations', userId, activeShop?.id, role],
    queryFn: async () => {
      if (!userId || !activeShop?.id) return [];

      const { data, error } = await supabase
        .from('role_training_recommendations')
        .select(`
          *,
          artifact:training_artifacts(id, title, artifact_type, access_url, is_active)
        `)
        .eq('user_id', userId)
        .eq('shop_id', activeShop.id)
        .eq('role', role)
        .eq('status', 'active')
        .order('priority', { ascending: true })
        .limit(10);

      if (error) throw error;

      return (data || []).map((rec): RoleTrainingRecommendation => ({
        id: rec.id,
        userId: rec.user_id,
        shopId: rec.shop_id,
        role: rec.role as 'advisor' | 'manager',
        artifactId: rec.artifact_id,
        categoryKey: rec.category_key,
        reason: rec.reason,
        priority: rec.priority || 50,
        status: rec.status as 'active' | 'dismissed' | 'completed',
        sourceEventId: rec.source_event_id,
        createdAt: rec.created_at,
        actionedAt: rec.actioned_at,
        // Core-100 R4 CORE-O: retired training is not offered as a current recommendation link.
        artifact: rec.artifact && (rec.artifact as { is_active?: boolean | null }).is_active !== false ? {
          id: rec.artifact.id,
          title: rec.artifact.title,
          artifactType: rec.artifact.artifact_type,
          accessUrl: rec.artifact.access_url,
        } : undefined,
      }));
    },
    enabled: !!userId && !!activeShop?.id,
  });
}

export function useDismissRecommendation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (recommendationId: string) => {
      const { error } = await supabase
        .from('role_training_recommendations')
        .update({ 
          status: 'dismissed',
          actioned_at: clock.now().toISOString(),
        })
        .eq('id', recommendationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role-training-recommendations'] });
    },
  });
}

export function useCompleteRecommendation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (recommendationId: string) => {
      const { error } = await supabase
        .from('role_training_recommendations')
        .update({ 
          status: 'completed',
          actioned_at: clock.now().toISOString(),
        })
        .eq('id', recommendationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['role-training-recommendations'] });
    },
  });
}
